import { useCallback, useEffect, useState } from "react";
import AdminShell from "@/components/feature/AdminShell";
import VideoUploader from "@/components/feature/VideoUploader";
import {
  fetchNudgeRules,
  fetchWebinarLeads,
  fetchWebinarOverview,
  fetchWebinarSettings,
  saveNudgeRule,
  saveWebinarSettings,
  SOURCE_LABELS,
  TEMP_META,
  type NudgeRule,
  type WebinarLeadRow,
  type WebinarOverview,
  type WebinarSettings,
} from "@/services/webinar";

const inputCls = "w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400";
const cardCls = "bg-background-100 border border-background-200 rounded-2xl p-5";
const PUBLIC_URL = "https://app.vivelo-ai.com/webinar";

const BUCKET_LABEL: Record<number, string> = {
  25: "Vio ~25 % y se fue",
  50: "Vio ~50 % y se fue",
  75: "Vio ~75 % y se fue",
};

const delayLabel = (m: number) => (m % 1440 === 0 ? `${m / 1440} día${m === 1440 ? "" : "s"}` : m % 60 === 0 ? `${m / 60} h` : `${m} min`);

function Kpi({ icon, label, value, hint }: { icon: string; label: string; value: string | number; hint?: string }) {
  return (
    <div className="bg-background-100 border border-background-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-xs text-foreground-500">
        <i className={`${icon} text-base text-primary-600`}></i>
        {label}
      </div>
      <div className="font-heading font-extrabold text-2xl text-foreground-950 mt-1">{value}</div>
      {hint ? <div className="text-[11px] text-foreground-500 mt-0.5">{hint}</div> : null}
    </div>
  );
}

function RuleEditor({ rule, onSaved }: { rule: NudgeRule; onSaved: (msg: string) => void }) {
  const [r, setR] = useState(rule);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(r) !== JSON.stringify(rule);
  const preview = r.template
    .replace(/\{nombre\}/g, "Laura")
    .replace(/\{porcentaje\}/g, String(r.bucket + 2))
    .replace(/\{resta\}/g, String(98 - r.bucket))
    .replace(/\{socio\}/g, "Wilmer")
    .replace(/\{enlace\}/g, "app.vivelo-ai.com/webinar?t=…");
  return (
    <div className={`rounded-xl border p-4 ${r.enabled ? "border-background-200 bg-background-50" : "border-dashed border-background-300 bg-background-100 opacity-70"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-heading font-bold text-sm text-foreground-950">
          Mensaje {r.step} <span className="font-normal text-foreground-500">· si pasan</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-foreground-600 flex items-center gap-1.5">
            <select value={r.delayMinutes} onChange={(e) => setR({ ...r, delayMinutes: Number(e.target.value) })} className="bg-background-100 border border-background-200 rounded-lg px-2 py-1 text-xs">
              {[...new Set([15, 30, 60, 120, 240, 720, 1440, 2880, 4320, r.delayMinutes])].sort((a, b) => a - b).map((m) => (
                <option key={m} value={m}>
                  {delayLabel(m)}
                </option>
              ))}
            </select>
            sin volver
          </label>
          <label className="text-xs text-foreground-600 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={r.enabled} onChange={(e) => setR({ ...r, enabled: e.target.checked })} className="w-4 h-4 accent-primary-500" />
            Activo
          </label>
        </div>
      </div>
      <textarea value={r.template} onChange={(e) => setR({ ...r, template: e.target.value })} rows={3} maxLength={900} className={`${inputCls} mt-3 resize-y`} aria-label="Texto del mensaje" />
      <div className="mt-2 text-[11px] text-foreground-500">
        Variables: <code>{"{nombre}"}</code> <code>{"{porcentaje}"}</code> <code>{"{resta}"}</code> <code>{"{socio}"}</code> <code>{"{enlace}"}</code>
      </div>
      <div className="mt-2 rounded-lg bg-emerald-50 dark:bg-background-200 border border-emerald-200 dark:border-background-300 px-3 py-2 text-xs text-foreground-800 whitespace-pre-wrap">{preview}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={r.templateName} onChange={(e) => setR({ ...r, templateName: e.target.value })} placeholder="Plantilla de WhatsApp (opcional)" className={`${inputCls} !w-64`} aria-label="Nombre de plantilla de WhatsApp" />
        <button
          disabled={!dirty || busy || r.template.trim().length < 10}
          onClick={async () => {
            setBusy(true);
            try {
              await saveNudgeRule(r);
              onSaved("Mensaje guardado");
            } catch {
              onSaved("No se pudo guardar");
            } finally {
              setBusy(false);
            }
          }}
          className="px-4 py-2 rounded-full text-xs font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-40 cursor-pointer whitespace-nowrap"
        >
          Guardar
        </button>
      </div>
    </div>
  );
}

export default function AdminWebinar() {
  const [settings, setSettings] = useState<WebinarSettings | null>(null);
  const [rules, setRules] = useState<NudgeRule[]>([]);
  const [overview, setOverview] = useState<WebinarOverview | null>(null);
  const [leads, setLeads] = useState<WebinarLeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [durMin, setDurMin] = useState("");

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2200);
  };

  const load = useCallback(async () => {
    const [s, r, o, l] = await Promise.all([fetchWebinarSettings(), fetchNudgeRules(), fetchWebinarOverview(30), fetchWebinarLeads(100)]);
    setSettings(s);
    setDurMin(s?.videoDuration ? String(Math.round(s.videoDuration / 60)) : "");
    setRules(r);
    setOverview(o);
    setLeads(l);
  }, []);

  useEffect(() => {
    let active = true;
    load()
      .catch((e) => active && setError(e instanceof Error ? e.message : "No se pudo cargar el webinar."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load]);

  const saveSettings = async () => {
    if (!settings) return;
    const mins = durMin.trim() ? Number(durMin) : null;
    if (mins !== null && (!Number.isFinite(mins) || mins < 1 || mins > 120)) return showToast("La duración debe estar entre 1 y 120 minutos.");
    if (settings.sendToHour <= settings.sendFromHour) return showToast("La hora final debe ser mayor que la inicial.");
    if (settings.videoUrl.trim() && !/^https?:\/\//i.test(settings.videoUrl.trim())) return showToast("El enlace del video debe empezar con https://");
    setSaving(true);
    try {
      await saveWebinarSettings({ ...settings, videoDuration: mins ? Math.round(mins * 60) : null });
      showToast("Ajustes guardados");
    } catch {
      showToast("No se pudieron guardar los ajustes.");
    } finally {
      setSaving(false);
    }
  };

  const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const o = overview;
  const funnel = o
    ? [
        { label: "Registrados", n: o.leads },
        { label: "Dieron play", n: o.started },
        { label: "Llegaron al 25 %", n: o.p25 },
        { label: "Llegaron al 50 %", n: o.p50 },
        { label: "Llegaron al 75 %", n: o.p75 },
        { label: "Terminaron", n: o.completed },
      ]
    : [];

  return (
    <AdminShell title="Webinar" description="Landing pública con registro, seguimiento del video y mensajes automáticos de WhatsApp para quien se queda a medias.">
      {loading ? (
        <div className="py-16 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      ) : error || !settings || !o ? (
        <div className="border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">{error ?? "No se encontró la configuración del webinar."}</div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className={`${cardCls} flex flex-wrap items-center gap-3`}>
            <div className="flex-1 min-w-[14rem]">
              <div className="text-xs text-foreground-500">Enlace público</div>
              <div className="font-mono text-sm text-foreground-950 break-all">{PUBLIC_URL}</div>
              <div className="text-[11px] text-foreground-500 mt-0.5">Para atribuir a un socio, agrega <code>?ref=CODIGO_DEL_SOCIO</code>.</div>
            </div>
            <button onClick={() => navigator.clipboard.writeText(PUBLIC_URL).then(() => showToast("Enlace copiado"))} className="px-4 py-2 rounded-full text-sm font-semibold border border-background-300 hover:bg-background-200 cursor-pointer whitespace-nowrap">
              <i className="ri-file-copy-line mr-1"></i>Copiar
            </button>
            <a href="/webinar" target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 cursor-pointer whitespace-nowrap">
              <i className="ri-external-link-line mr-1"></i>Abrir
            </a>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon="ri-user-add-line" label="Registrados (30 días)" value={o.leads} hint={`${o.optedIn} aceptaron WhatsApp`} />
            <Kpi icon="ri-play-circle-line" label="Terminaron el video" value={`${pct(o.completed, o.leads)}%`} hint={`${o.completed} personas`} />
            <Kpi icon="ri-whatsapp-line" label="Mensajes enviados" value={o.nudgesSent} hint={`${o.nudgesPending} en cola · ${o.nudgesFailed} con error`} />
            <Kpi icon="ri-arrow-go-back-line" label="Volvieron tras un mensaje" value={o.recovered} hint={`${o.recoveredCompleted} lo terminaron`} />
          </div>

          <div className={cardCls}>
            <h2 className="font-heading font-bold text-foreground-950">Embudo del video</h2>
            <ul className="mt-4 flex flex-col gap-2.5">
              {funnel.map((f) => (
                <li key={f.label} className="grid grid-cols-[9.5rem_1fr_5rem] items-center gap-3 text-sm">
                  <span className="text-foreground-700">{f.label}</span>
                  <span className="h-2.5 rounded-full bg-background-200 overflow-hidden">
                    <span className="block h-full rounded-full bg-primary-500" style={{ width: `${pct(f.n, o.leads)}%` }}></span>
                  </span>
                  <span className="text-right text-foreground-600 tabular-nums">
                    {f.n} <span className="text-foreground-400">({pct(f.n, o.leads)}%)</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-background-200 px-3 py-1">Clic en “Descubrir mi perfil”: <strong>{o.ctaTest}</strong></span>
              <span className="rounded-full bg-background-200 px-3 py-1">Clic en “Conocer la comunidad”: <strong>{o.ctaCommunity}</strong></span>
              <span className="rounded-full bg-background-200 px-3 py-1">Calientes / muy calientes: <strong>{o.hot}</strong></span>
            </div>
          </div>

          <div className={cardCls}>
            <h2 className="font-heading font-bold text-foreground-950">Ajustes del webinar</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="block text-xs font-semibold text-foreground-600 md:col-span-2">
                Título
                <input value={settings.title} onChange={(e) => setSettings({ ...settings, title: e.target.value })} maxLength={120} className={`${inputCls} mt-1`} />
              </label>
              <label className="block text-xs font-semibold text-foreground-600 md:col-span-2">
                Subtítulo
                <input value={settings.subtitle} onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })} maxLength={240} className={`${inputCls} mt-1`} />
              </label>
              <div className="md:col-span-2">
                <div className="text-xs font-semibold text-foreground-600 mb-1">Subir el video del webinar (queda guardado en Cloudflare)</div>
                <VideoUploader
                  kind="webinar"
                  onUploaded={(r) => {
                    setSettings((cur) => (cur ? { ...cur, videoUrl: r.url } : cur));
                    if (r.durationSeconds) setDurMin(String(Math.max(1, Math.round(r.durationSeconds / 60))));
                    showToast("Video subido. Pulsa Guardar para publicarlo en la landing.");
                  }}
                />
                <p className="mt-1 text-[11px] text-foreground-500">O pega un enlace abajo (YouTube u otro .mp4). Subir un archivo reemplaza el enlace actual.</p>
              </div>
              <label className="block text-xs font-semibold text-foreground-600 md:col-span-2">
                Enlace del video (YouTube o archivo .mp4/.webm)
                <input value={settings.videoUrl} onChange={(e) => setSettings({ ...settings, videoUrl: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className={`${inputCls} mt-1`} />
              </label>
              <label className="block text-xs font-semibold text-foreground-600">
                Duración del video (minutos)
                <input value={durMin} onChange={(e) => setDurMin(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="Ej. 10" className={`${inputCls} mt-1`} />
              </label>
              <label className="block text-xs font-semibold text-foreground-600">
                Máximo de mensajes por persona
                <input value={settings.maxNudges} onChange={(e) => setSettings({ ...settings, maxNudges: Math.min(10, Number(e.target.value.replace(/\D/g, "")) || 0) })} inputMode="numeric" className={`${inputCls} mt-1`} />
              </label>
              <label className="block text-xs font-semibold text-foreground-600">
                Enviar desde las (hora local de la persona)
                <input value={settings.sendFromHour} onChange={(e) => setSettings({ ...settings, sendFromHour: Math.min(23, Number(e.target.value.replace(/\D/g, "")) || 0) })} inputMode="numeric" className={`${inputCls} mt-1`} />
              </label>
              <label className="block text-xs font-semibold text-foreground-600">
                Hasta las
                <input value={settings.sendToHour} onChange={(e) => setSettings({ ...settings, sendToHour: Math.min(24, Number(e.target.value.replace(/\D/g, "")) || 0) })} inputMode="numeric" className={`${inputCls} mt-1`} />
              </label>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="block text-xs font-semibold text-foreground-600">
                Cuándo pedir el WhatsApp
                <select
                  value={settings.captureMode}
                  onChange={(e) => setSettings({ ...settings, captureMode: e.target.value === "before_video" ? "before_video" : "mid_video" })}
                  className={`${inputCls} mt-1`}
                >
                  <option value="mid_video">A mitad del video (menos fricción, recomendado para anuncios)</option>
                  <option value="before_video">Antes de ver el video</option>
                </select>
              </label>
              {settings.captureMode === "mid_video" ? (
                <label className="block text-xs font-semibold text-foreground-600">
                  Pausar y pedirlo al llegar a (% del video)
                  <input
                    value={settings.captureAtPercent}
                    onChange={(e) => setSettings({ ...settings, captureAtPercent: Math.min(90, Number(e.target.value.replace(/\D/g, "")) || 0) })}
                    inputMode="numeric"
                    className={`${inputCls} mt-1`}
                  />
                </label>
              ) : null}
            </div>
            <div className="mt-4 flex flex-col gap-2">
              {[
                { k: "enabled" as const, l: "Webinar publicado (la landing está activa)" },
                { k: "requireRegistration" as const, l: "Pedir nombre y WhatsApp (necesario para el seguimiento y los mensajes; si lo apagas, el video es libre y sin seguimiento)" },
                { k: "nudgesEnabled" as const, l: "Enviar mensajes de WhatsApp a quien se quede a medias" },
              ].map((t) => (
                <label key={t.k} className="flex items-start gap-2 text-sm text-foreground-700 cursor-pointer">
                  <input type="checkbox" checked={settings[t.k]} onChange={(e) => setSettings({ ...settings, [t.k]: e.target.checked })} className="w-4 h-4 mt-0.5 accent-primary-500" />
                  {t.l}
                </label>
              ))}
            </div>
            <button onClick={saveSettings} disabled={saving} className="mt-4 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-60 cursor-pointer whitespace-nowrap">
              {saving ? "Guardando…" : "Guardar ajustes"}
            </button>
          </div>

          <div className={cardCls}>
            <h2 className="font-heading font-bold text-foreground-950">Mensajes de recuperación por WhatsApp</h2>
            <p className="text-sm text-foreground-600 mt-1">
              Si alguien vio una parte del webinar y no lo terminó, recibe un mensaje con su porcentaje y un enlace que lo lleva al punto donde se quedó. Se detiene si termina el video o responde STOP. Se envía solo en el horario configurado.
            </p>
            <div className="mt-4 flex flex-col gap-5">
              {[25, 50, 75].map((b) => (
                <div key={b}>
                  <h3 className="text-sm font-heading font-bold text-foreground-950 mb-2">{BUCKET_LABEL[b]}</h3>
                  <div className="flex flex-col gap-3">
                    {rules
                      .filter((r) => r.bucket === b)
                      .map((r) => (
                        <RuleEditor key={r.id} rule={r} onSaved={(m) => { showToast(m); void load(); }} />
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={cardCls}>
            <h2 className="font-heading font-bold text-foreground-950">Personas registradas</h2>
            {leads.length === 0 ? (
              <p className="mt-3 text-sm text-foreground-500">Aún no hay registros. Comparte el enlace del webinar para empezar.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm min-w-[40rem]">
                  <thead>
                    <tr className="text-left text-xs text-foreground-500 border-b border-background-200">
                      <th className="py-2 pr-3 font-semibold">Persona</th>
                      <th className="py-2 pr-3 font-semibold">Visto</th>
                      <th className="py-2 pr-3 font-semibold">Temperatura</th>
                      <th className="py-2 pr-3 font-semibold">WhatsApp</th>
                      <th className="py-2 pr-3 font-semibold">Socio</th>
                      <th className="py-2 pr-3 font-semibold">Canal</th>
                      <th className="py-2 font-semibold">Última actividad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-b border-background-200 last:border-b-0">
                        <td className="py-2.5 pr-3">
                          <div className="font-semibold text-foreground-950">{l.name}</div>
                          <div className="text-[11px] text-foreground-500">{l.country ?? "—"} · ···{l.phoneLast4}</div>
                        </td>
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-2">
                            <span className="w-16 h-1.5 rounded-full bg-background-200 overflow-hidden">
                              <span className="block h-full bg-primary-500" style={{ width: `${l.maxPercent}%` }}></span>
                            </span>
                            <span className="text-xs tabular-nums text-foreground-700">{l.completed ? "100%" : `${l.maxPercent}%`}</span>
                          </div>
                        </td>
                        <td className="py-2.5 pr-3">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TEMP_META[l.temperature]?.cls ?? ""}`}>{TEMP_META[l.temperature]?.label ?? l.temperature}</span>
                          <span className="text-[11px] text-foreground-500 ml-1.5">{l.score} pts</span>
                        </td>
                        <td className="py-2.5 pr-3 text-xs">{l.optedIn ? <span className="text-emerald-700">Acepta</span> : <span className="text-foreground-500">No</span>}</td>
                        <td className="py-2.5 pr-3 text-xs text-foreground-600">{l.sponsorName ?? "—"}</td>
                        <td className="py-2.5 pr-3 text-xs text-foreground-600">{l.source ? SOURCE_LABELS[l.source] ?? l.source : "—"}</td>
                        <td className="py-2.5 text-xs text-foreground-600">{new Date(l.lastActivity).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {toast ? <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-background-950 text-foreground-50 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AdminShell>
  );
}
